import api from './api';
import { AdminUser, AdminRole } from './authService';
import { PaginatedResponse, PaginationMeta } from './wineRegionService';

// API endpoints for admin user management
const API_ENDPOINTS = {
  ADMIN_USERS: '/admin/admin-user',
  ADMIN_USER: '/admin/admin-user', // Endpoint for single user operations
};

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: AdminRole;
  password: string;
  passwordChangeRequired?: boolean;
  // Required (>=1) when role === 'VENUE_USER'. Ignored for other roles.
  assignedVenueIds?: string[];
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: AdminRole;
  active?: boolean;
  assignedVenueIds?: string[];
}

const userService = {
  /**
   * Get all admin users with pagination support
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param sort Field to sort by
   * @param order Sort order ('asc' or 'desc')
   * @returns Paginated response with admin users
   */
  getAllUsers: async (page = 1, pageSize = 20, sort = 'email', order = 'asc'): Promise<PaginatedResponse<AdminUser>> => {
    const response = await api.get(API_ENDPOINTS.ADMIN_USERS, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get a specific admin user by ID
   */
  getUserById: async (userId: string): Promise<AdminUser> => {
    const response = await api.get(`${API_ENDPOINTS.ADMIN_USER}/${userId}`);
    return response.data;
  },

  /**
   * Create a new admin user.
   *
   * Backend wraps the response as `{ user, initialPassword, message }` (see
   * adminUserController.createAdminUser). Without unwrapping, callers see an
   * AdminUser whose _id is undefined — UserCreate's post-save redirect then
   * navigates to `/admin/users/undefined`. Mocks / tests may return the user
   * directly, so fall back if `.user` is absent.
   */
  createUser: async (userData: CreateUserRequest): Promise<AdminUser> => {
    const response = await api.post(API_ENDPOINTS.ADMIN_USERS, userData);
    const data = response.data as any;
    return (data && data.user) ? data.user : data;
  },

  /**
   * Update an existing admin user
   */
  updateUser: async (userId: string, userData: UpdateUserRequest): Promise<AdminUser> => {
    const response = await api.put(`${API_ENDPOINTS.ADMIN_USER}/${userId}`, userData);
    return response.data;
  },

  /**
   * Delete an admin user
   */
  deleteUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.delete(`${API_ENDPOINTS.ADMIN_USER}/${userId}`);
    return response.data;
  },

  /**
   * Reset password for a user (admin function)
   */
  resetUserPassword: async (userId: string, newPassword: string, passwordChangeRequired: boolean = true): Promise<{ message: string }> => {
    const response = await api.post(`${API_ENDPOINTS.ADMIN_USER}/${userId}/reset-password`, {
      newPassword,
      passwordChangeRequired
    });
    return response.data;
  }
};

export default userService;
